// Curated library of royalty-free background tracks for the create-video
// page. Files live on R2 under https://files.easy-peasy.ai/music/ — kept
// off the Next.js bundle so the deploy stays small and Lambda renderers
// can fetch them directly. Categorised so the picker can surface a filter
// row above the list.

export type LibraryCategory =
  | 'vlog'
  | 'cinematic'
  | 'chill'
  | 'hype'
  | 'fun'
  | 'corporate'
  | 'inspirational'
  | 'specialty';

export interface LibraryTrack {
  /** Stable id used in API payloads and UI selection state. */
  id: string;
  /** Friendly title shown to the user. */
  title: string;
  /** Short vibe word — appears alongside duration in the row. */
  vibe: string;
  /** One-word mood, threaded through to the summary card. */
  mood: string;
  /** Filter group for the chip row above the list. */
  category: LibraryCategory;
  /** Absolute R2 CDN URL (https://files.easy-peasy.ai/music/...). */
  url: string;
  /** Duration in seconds — renders as "M:SS" in the row. */
  durationSeconds: number;
  /**
   * Composer when CC-BY attribution is required (rendered inline). Omit for
   * tracks that don't need a visible credit.
   */
  composer?: string;
}

export const LIBRARY_CATEGORIES: Array<{
  id: LibraryCategory | 'all';
  label: string;
}> = [
  { id: 'all', label: 'All' },
  { id: 'vlog', label: 'Vlog' },
  { id: 'cinematic', label: 'Cinematic' },
  { id: 'chill', label: 'Chill' },
  { id: 'hype', label: 'Hype' },
  { id: 'inspirational', label: 'Inspirational' },
  { id: 'fun', label: 'Fun' },
  { id: 'corporate', label: 'Corporate' },
  { id: 'specialty', label: 'Specialty' },
];

export const MUSIC_LIBRARY: LibraryTrack[] = [
  // ── Vlog / Travel / IG edits ───────────────────────────────────────
  {
    id: 'lib-tourist',
    title: 'Tourist',
    vibe: 'Travel',
    mood: 'uplifting',
    category: 'vlog',
    url: 'https://files.easy-peasy.ai/music/tourist.mp3',
    durationSeconds: 170,
  },
  {
    id: 'lib-for-her-summer',
    title: 'For Her — Summer Vlog',
    vibe: 'Chill upbeat',
    mood: 'happy',
    category: 'vlog',
    url: 'https://files.easy-peasy.ai/music/for-her-summer-travel-vlog.mp3',
    durationSeconds: 133,
  },
  {
    id: 'lib-refresher',
    title: 'Refresher',
    vibe: 'Upbeat',
    mood: 'energetic',
    category: 'vlog',
    url: 'https://files.easy-peasy.ai/music/refresher.mp3',
    durationSeconds: 116,
  },
  {
    id: 'lib-invitation',
    title: 'Invitation',
    vibe: 'Inviting',
    mood: 'warm',
    category: 'vlog',
    url: 'https://files.easy-peasy.ai/music/invitation.mp3',
    durationSeconds: 159,
  },
  {
    id: 'lib-paris-else',
    title: 'Paris Else',
    vibe: 'Aesthetic',
    mood: 'dreamy',
    category: 'vlog',
    url: 'https://files.easy-peasy.ai/music/paris-else.mp3',
    durationSeconds: 24,
  },
  {
    id: 'lib-beauteous-electronic',
    title: 'Beauteous Electronic',
    vibe: 'Upbeat',
    mood: 'bright',
    category: 'vlog',
    url: 'https://files.easy-peasy.ai/music/beauteous-upbeat-electronic.mp3',
    durationSeconds: 111,
  },
  {
    id: 'lib-upbeat-vlog',
    title: 'Carefree Vlog',
    vibe: 'Aesthetic',
    mood: 'happy',
    category: 'vlog',
    url: 'https://files.easy-peasy.ai/music/upbeat-vlog.mp3',
    durationSeconds: 205,
    composer: 'Kevin MacLeod',
  },

  // ── Cinematic / Atmospheric ────────────────────────────────────────
  {
    id: 'lib-observer',
    title: 'Observer',
    vibe: 'Atmospheric',
    mood: 'mysterious',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/observer.mp3',
    durationSeconds: 177,
  },
  {
    id: 'lib-bladerunner-remix',
    title: 'Bladerunner Remix',
    vibe: 'Synthwave',
    mood: 'neon',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/bladerunner-remix.mp3',
    durationSeconds: 63,
  },
  {
    id: 'lib-painted-night',
    title: 'The Painted Night',
    vibe: 'Cinematic',
    mood: 'epic',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/the-painted-night.mp3',
    durationSeconds: 178,
  },
  {
    id: 'lib-dark-sci-fi',
    title: 'Dark Sci-Fi',
    vibe: 'Sci-fi',
    mood: 'dark',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/dark-sci-fi.mp3',
    durationSeconds: 64,
  },
  {
    id: 'lib-snowfall',
    title: 'Snowfall',
    vibe: 'Ambient',
    mood: 'serene',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/snowfall.mp3',
    durationSeconds: 19,
  },
  {
    id: 'lib-video-game-boss',
    title: 'Video Game Boss',
    vibe: 'Epic gaming',
    mood: 'intense',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/video-game-boss.mp3',
    durationSeconds: 75,
  },
  {
    id: 'lib-epic-cinematic',
    title: 'Epic Cinematic',
    vibe: 'Cinematic',
    mood: 'epic',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/epic-cinematic.mp3',
    durationSeconds: 231,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-mysterious-spy',
    title: 'Mysterious Spy',
    vibe: 'Mystery',
    mood: 'mysterious',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/mysterious-spy.mp3',
    durationSeconds: 227,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-dreamy-flashback',
    title: 'Dreamy Flashback',
    vibe: 'Dreamy',
    mood: 'nostalgic',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/dreamy-flashback.mp3',
    durationSeconds: 127,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-five-armies',
    title: 'Five Armies',
    vibe: 'Epic battle',
    mood: 'heroic',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/five-armies.mp3',
    durationSeconds: 156,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-hidden-agenda',
    title: 'Hidden Agenda',
    vibe: 'Suspense',
    mood: 'tense',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/hidden-agenda.mp3',
    durationSeconds: 135,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-lightless-dawn',
    title: 'Lightless Dawn',
    vibe: 'Atmospheric',
    mood: 'ethereal',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/lightless-dawn.mp3',
    durationSeconds: 380,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-investigations',
    title: 'Investigations',
    vibe: 'Detective',
    mood: 'curious',
    category: 'cinematic',
    url: 'https://files.easy-peasy.ai/music/investigations.mp3',
    durationSeconds: 94,
    composer: 'Kevin MacLeod',
  },

  // ── Chill / Lofi / Piano ───────────────────────────────────────────
  {
    id: 'lib-lazy-sunday-lofi',
    title: 'Lazy Sunday Lo-Fi',
    vibe: 'Lo-fi chill',
    mood: 'calm',
    category: 'chill',
    url: 'https://files.easy-peasy.ai/music/lazy-sunday-chill-lofi.mp3',
    durationSeconds: 243,
  },
  {
    id: 'lib-soft-piano',
    title: 'Soft Piano',
    vibe: 'Piano',
    mood: 'tender',
    category: 'chill',
    url: 'https://files.easy-peasy.ai/music/soft-piano-background.mp3',
    durationSeconds: 124,
  },
  {
    id: 'lib-sleepless',
    title: 'Sleepless',
    vibe: 'Late night',
    mood: 'reflective',
    category: 'chill',
    url: 'https://files.easy-peasy.ai/music/sleepless.mp3',
    durationSeconds: 146,
  },
  {
    id: 'lib-nights-like-this',
    title: 'Nights Like This',
    vibe: 'Chill night',
    mood: 'mellow',
    category: 'chill',
    url: 'https://files.easy-peasy.ai/music/nights-like-this.mp3',
    durationSeconds: 84,
  },
  {
    id: 'lib-lofi-chill',
    title: 'Slow Burn',
    vibe: 'Chill',
    mood: 'calm',
    category: 'chill',
    url: 'https://files.easy-peasy.ai/music/lofi-chill.mp3',
    durationSeconds: 229,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-emotional-piano',
    title: 'Emotional Piano',
    vibe: 'Emotional',
    mood: 'tender',
    category: 'chill',
    url: 'https://files.easy-peasy.ai/music/emotional-piano.mp3',
    durationSeconds: 239,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-wallpaper',
    title: 'Wallpaper',
    vibe: 'Underscore',
    mood: 'minimal',
    category: 'chill',
    url: 'https://files.easy-peasy.ai/music/wallpaper.mp3',
    durationSeconds: 220,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-thinking-music',
    title: 'Thinking Music',
    vibe: 'Background',
    mood: 'contemplative',
    category: 'chill',
    url: 'https://files.easy-peasy.ai/music/thinking-music.mp3',
    durationSeconds: 196,
    composer: 'Kevin MacLeod',
  },

  // ── Hype / Energetic / Drill ──────────────────────────────────────
  {
    id: 'lib-hype-drill',
    title: 'Hype Drill',
    vibe: 'Drill',
    mood: 'energetic',
    category: 'hype',
    url: 'https://files.easy-peasy.ai/music/hype-drill.mp3',
    durationSeconds: 236,
  },
  {
    id: 'lib-ill-do-it',
    title: "I'll Do It",
    vibe: 'Hype',
    mood: 'fierce',
    category: 'hype',
    url: 'https://files.easy-peasy.ai/music/ill-do-it.mp3',
    durationSeconds: 87,
  },
  {
    id: 'lib-nasty-jamz',
    title: 'Nasty Jamz',
    vibe: 'Trap',
    mood: 'gritty',
    category: 'hype',
    url: 'https://files.easy-peasy.ai/music/nasty-jamz.mp3',
    durationSeconds: 100,
  },
  {
    id: 'lib-fluidity-ig',
    title: 'Fluidity IG Edit',
    vibe: 'IG edit',
    mood: 'energetic',
    category: 'hype',
    url: 'https://files.easy-peasy.ai/music/fluidity-ig-edit.mp3',
    durationSeconds: 61,
  },
  {
    id: 'lib-energetic-edm',
    title: 'Energetic EDM',
    vibe: 'EDM',
    mood: 'energetic',
    category: 'hype',
    url: 'https://files.easy-peasy.ai/music/energetic-edm.mp3',
    durationSeconds: 366,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-hot-pursuit',
    title: 'Hot Pursuit',
    vibe: 'Chase',
    mood: 'intense',
    category: 'hype',
    url: 'https://files.easy-peasy.ai/music/hot-pursuit.mp3',
    durationSeconds: 169,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-voltaic',
    title: 'Voltaic',
    vibe: 'Synth',
    mood: 'driving',
    category: 'hype',
    url: 'https://files.easy-peasy.ai/music/voltaic.mp3',
    durationSeconds: 196,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-volatile-reaction',
    title: 'Volatile Reaction',
    vibe: 'High energy',
    mood: 'aggressive',
    category: 'hype',
    url: 'https://files.easy-peasy.ai/music/volatile-reaction.mp3',
    durationSeconds: 165,
    composer: 'Kevin MacLeod',
  },

  // ── Inspirational / Motivational ──────────────────────────────────
  {
    id: 'lib-skys-the-limit',
    title: "Sky's The Limit",
    vibe: 'Motivational',
    mood: 'uplifting',
    category: 'inspirational',
    url: 'https://files.easy-peasy.ai/music/skys-the-limit.mp3',
    durationSeconds: 134,
  },
  {
    id: 'lib-powerful-motivational',
    title: 'Powerful Motivational',
    vibe: 'Powerful',
    mood: 'driving',
    category: 'inspirational',
    url: 'https://files.easy-peasy.ai/music/powerful-motivational.mp3',
    durationSeconds: 145,
  },
  {
    id: 'lib-titanium',
    title: 'Titanium',
    vibe: 'Anthemic',
    mood: 'triumphant',
    category: 'inspirational',
    url: 'https://files.easy-peasy.ai/music/titanium.mp3',
    durationSeconds: 106,
  },
  {
    id: 'lib-soaring',
    title: 'Soaring',
    vibe: 'Uplifting',
    mood: 'hopeful',
    category: 'inspirational',
    url: 'https://files.easy-peasy.ai/music/soaring.mp3',
    durationSeconds: 380,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-fearless-first',
    title: 'Fearless First',
    vibe: 'Anthemic',
    mood: 'bold',
    category: 'inspirational',
    url: 'https://files.easy-peasy.ai/music/fearless-first.mp3',
    durationSeconds: 196,
    composer: 'Kevin MacLeod',
  },

  // ── Fun / Playful / Quirky ────────────────────────────────────────
  {
    id: 'lib-cartoon-music',
    title: 'Cartoon Music',
    vibe: 'Cartoon',
    mood: 'playful',
    category: 'fun',
    url: 'https://files.easy-peasy.ai/music/cartoon-music.mp3',
    durationSeconds: 68,
  },
  {
    id: 'lib-happy-mood-ukulele',
    title: 'Happy Ukulele',
    vibe: 'Sunny',
    mood: 'cheerful',
    category: 'fun',
    url: 'https://files.easy-peasy.ai/music/happy-mood-ukulele.mp3',
    durationSeconds: 54,
  },
  {
    id: 'lib-sparky-deathcap',
    title: 'Sparky Deathcap',
    vibe: 'Indie',
    mood: 'quirky',
    category: 'fun',
    url: 'https://files.easy-peasy.ai/music/sparky-deathcap.mp3',
    durationSeconds: 190,
  },
  {
    id: 'lib-playful-meme',
    title: 'Sneaky Snitch',
    vibe: 'Meme',
    mood: 'quirky',
    category: 'fun',
    url: 'https://files.easy-peasy.ai/music/playful-meme.mp3',
    durationSeconds: 137,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-happy-bouncy',
    title: 'Hyperfun',
    vibe: 'Bouncy',
    mood: 'cheerful',
    category: 'fun',
    url: 'https://files.easy-peasy.ai/music/happy-bouncy.mp3',
    durationSeconds: 233,
    composer: 'Kevin MacLeod',
  },
  {
    id: 'lib-pinball-spring',
    title: 'Pinball Spring',
    vibe: 'Bouncy',
    mood: 'playful',
    category: 'fun',
    url: 'https://files.easy-peasy.ai/music/pinball-spring.mp3',
    durationSeconds: 236,
    composer: 'Kevin MacLeod',
  },

  // ── Corporate / Educational / Tech ─────────────────────────────────
  {
    id: 'lib-business-presentation',
    title: 'Business Presentation',
    vibe: 'Corporate',
    mood: 'professional',
    category: 'corporate',
    url: 'https://files.easy-peasy.ai/music/business-presentation.mp3',
    durationSeconds: 116,
  },
  {
    id: 'lib-corporate-background',
    title: 'Corporate Background',
    vibe: 'Corporate',
    mood: 'professional',
    category: 'corporate',
    url: 'https://files.easy-peasy.ai/music/corporate-background.mp3',
    durationSeconds: 196,
  },
  {
    id: 'lib-creative-tech-showreel',
    title: 'Creative Tech Showreel',
    vibe: 'Tech',
    mood: 'modern',
    category: 'corporate',
    url: 'https://files.easy-peasy.ai/music/creative-technology-showreel.mp3',
    durationSeconds: 110,
  },
  {
    id: 'lib-educational',
    title: 'Educational',
    vibe: 'Educational',
    mood: 'clear',
    category: 'corporate',
    url: 'https://files.easy-peasy.ai/music/educational.mp3',
    durationSeconds: 198,
  },
  {
    id: 'lib-corporate-motivational',
    title: 'Corporate Uplift',
    vibe: 'Motivational',
    mood: 'uplifting',
    category: 'corporate',
    url: 'https://files.easy-peasy.ai/music/corporate-motivational.mp3',
    durationSeconds: 118,
    composer: 'Kevin MacLeod',
  },

  // ── Specialty ──────────────────────────────────────────────────────
  {
    id: 'lib-country',
    title: 'Country',
    vibe: 'Country',
    mood: 'wholesome',
    category: 'specialty',
    url: 'https://files.easy-peasy.ai/music/country.mp3',
    durationSeconds: 240,
  },
  {
    id: 'lib-islamic-royalty-free',
    title: 'Islamic Royalty-Free',
    vibe: 'Islamic',
    mood: 'reverent',
    category: 'specialty',
    url: 'https://files.easy-peasy.ai/music/islamic-royalty-free.mp3',
    durationSeconds: 168,
  },
];

export function findLibraryTrack(id: string): LibraryTrack | undefined {
  return MUSIC_LIBRARY.find((t) => t.id === id);
}
